// ---- Pure firestarting / spark minigame sim ---------------------------------
// Pattern expansion + fail-meter judge over the shared lane primitives
// (src/sim/lane.ts — same math fishing/cooking/chopping use). Server handlers +
// the client minigame share these; rng stays OUTSIDE (seed is rolled by the
// caller). DOM/THREE/Bun-free. Materials spend is NOT here — bag consume is a
// success-path server concern only. No stamina (v1 cost = kindling + log).

import {
  genLanePattern, startLaneJudge, applyLaneResult, forceResolveLane,
  type LanePattern, type LaneJudge, type LaneEvent, type LaneFx,
} from "./lane.ts";

// ---- Defaults (tunables doc overrides via src/tunables.ts) -------------------

export const FIRE_PROMPT_COUNT = 3;
export const FIRE_GAP = 0.9;
export const FIRE_GAP_MIN = 0.55;
export const FIRE_HIT_WINDOW = 0.28;
export const FIRE_MISS_TENSION = 0.45;
export const FIRE_HIT_RELIEF = 0.1;
export const FIRE_INPUT_SLACK = 1.5;
export const FIRE_DEADLINE_PAD = 3;
export const FIRE_SESSION_TIMEOUT = 60;

export type FireStartTuning = {
  promptCount: number;
  gap: number;
  gapMin: number;
  hitWindow: number;
  missTension: number;
  hitRelief: number;
  inputSlack: number;
  deadlinePad: number;
  sessionTimeout: number;
};

export const FIRE_DEFAULTS: FireStartTuning = {
  promptCount: FIRE_PROMPT_COUNT,
  gap: FIRE_GAP,
  gapMin: FIRE_GAP_MIN,
  hitWindow: FIRE_HIT_WINDOW,
  missTension: FIRE_MISS_TENSION,
  hitRelief: FIRE_HIT_RELIEF,
  inputSlack: FIRE_INPUT_SLACK,
  deadlinePad: FIRE_DEADLINE_PAD,
  sessionTimeout: FIRE_SESSION_TIMEOUT,
};

export type FirePattern = LanePattern;
export type FireJudge = LaneJudge;
export type FireEvent = LaneEvent;

export function laneFx(tun: Pick<FireStartTuning, "missTension" | "hitRelief">): LaneFx {
  return { miss: tun.missTension, relief: tun.hitRelief };
}

/** Expand a seed into the spark prompt pattern (both sides, identical). */
export function genFirePattern(
  seed: number,
  tun: Pick<FireStartTuning, "promptCount" | "gap" | "gapMin">,
): FirePattern {
  return genLanePattern(seed, tun.promptCount, tun.gap, tun.gapMin);
}

export function startFireJudge(pattern: FirePattern, hitWindow: number): FireJudge {
  return startLaneJudge(pattern, hitWindow);
}

export function applyFireResult(
  j: FireJudge,
  hit: boolean,
  tun: Pick<FireStartTuning, "missTension" | "hitRelief">,
): FireEvent[] {
  return applyLaneResult(j, hit, laneFx(tun));
}

export function forceResolveFire(
  j: FireJudge,
  tun: Pick<FireStartTuning, "missTension" | "hitRelief">,
): FireEvent[] {
  return forceResolveLane(j, laneFx(tun));
}
